import React, { useMemo, useLayoutEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { aggregateStudentSessionsForPreset } from './studentSessionsChartsUtils';

const BAR_FILL = '#6366f1';
const BAR_FILL_ALT = '#8b5cf6';
const BAR_FILL_BUCKETS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#94a3b8'];
const CHART_H = 260;

const PRESET_TITLE_KEYS = {
  dailyExercises: {
    primary: 'reports.dailyExercisesApi.studentChartPrimaryTitle',
    secondary: 'reports.dailyExercisesApi.studentChartSecondaryTitle',
    tertiaryScore: 'reports.dailyExercisesApi.studentChartTertiaryScoreTitle',
    timeline: 'reports.dailyExercisesApi.studentChartTimelineTitle'
  },
  flashAnzan: {
    primary: 'reports.flashAnzanApi.studentChartPrimaryTitle',
    secondary: 'reports.flashAnzanApi.studentChartSecondaryTitle',
    tertiaryScore: 'reports.flashAnzanApi.studentChartTertiaryScoreTitle',
    timeline: 'reports.flashAnzanApi.studentChartTimelineTitle'
  },
  flashCards: {
    primary: 'reports.flashCardsApi.studentChartPrimaryTitle',
    secondary: 'reports.flashCardsApi.studentChartSecondaryTitle',
    tertiaryScore: 'reports.flashCardsApi.studentChartTertiaryScoreTitle',
    timeline: 'reports.flashCardsApi.studentChartTimelineTitle'
  },
  games: {
    primary: 'reports.gamesApi.studentChartPrimaryTitle',
    secondary: 'reports.gamesApi.studentChartSecondaryTitle',
    tertiaryScore: 'reports.gamesApi.studentChartTertiaryScoreTitle',
    timeline: 'reports.gamesApi.studentChartTimelineTitle'
  },
  challenges: {
    primary: 'reports.challengesApi.studentChartPrimaryTitle',
    secondary: 'reports.challengesApi.studentChartSecondaryTitle',
    tertiaryResult: 'reports.challengesApi.studentChartTertiaryResultTitle',
    timeline: 'reports.challengesApi.studentChartTimelineTitle'
  }
};

function bucketScoreLabel(t, key) {
  const map = {
    '0-25': 'chartBucket_0_25',
    '25-50': 'chartBucket_25_50',
    '50-75': 'chartBucket_50_75',
    '75-100': 'chartBucket_75_100',
    '?': 'chartBucket_unknown'
  };
  const k = map[key] || 'chartBucket_unknown';
  return t(`reports.dailyExercisesApi.${k}`);
}

function bandAxisLabel(t, name) {
  if (!name || name === '—') return '—';
  const k = String(name).toLowerCase();
  const key = `reports.dailyExercisesApi.sessionBand_${k}`;
  const tr = t(key);
  return tr === key ? name : tr;
}

/**
 * @param {object} props
 * @param {'dailyExercises'|'flashAnzan'|'flashCards'|'games'|'challenges'} props.preset
 * @param {Array} props.rows
 * @param {boolean} props.loading
 * @param {boolean} props.partial
 * @param {function} props.t
 * @param {function} [props.formatSecondaryName] — ترجمة المحور الثاني (مثل حالة التحدي)
 */
const StudentSessionReportCharts = ({ preset, rows, loading, partial, t, formatSecondaryName }) => {
  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [rows?.length]);

  const agg = useMemo(() => aggregateStudentSessionsForPreset(preset, rows || []), [preset, rows]);

  const titleKeys = PRESET_TITLE_KEYS[preset] || PRESET_TITLE_KEYS.dailyExercises;

  const secondaryFormatter = formatSecondaryName || ((name) => bandAxisLabel(t, name));

  const tertiaryScoreData = useMemo(() => {
    if (agg.tertiary?.type !== 'score' || !agg.tertiary.rows) return [];
    return agg.tertiary.rows.map((b) => ({
      ...b,
      label: bucketScoreLabel(t, b.key)
    }));
  }, [agg.tertiary, t]);

  const tertiaryResultData = useMemo(() => {
    if (agg.tertiary?.type !== 'result' || !agg.tertiary.rows) return [];
    return agg.tertiary.rows.map((r) => {
      let label = '—';
      if (r.key === '1') label = t('reports.challengesApi.resultCorrectTrue');
      else if (r.key === '0') label = t('reports.challengesApi.resultCorrectFalse');
      else label = t('reports.challengesApi.resultCorrectUnknown');
      return { ...r, label };
    });
  }, [agg.tertiary, t]);

  const sectionTitle = t('reports.dailyExercisesApi.chartsSectionTitle');
  const footnote = t('reports.dailyExercisesApi.chartsFootnote');
  const partialNote = t('reports.dailyExercisesApi.chartsPartialNote');
  const loadingMsg = t('reports.dailyExercisesApi.chartsLoading');
  const emptyMsg = t('reports.dailyExercisesApi.chartsEmpty');
  const countLabel = t('reports.dailyExercisesApi.chartSessionsCount');

  if (loading) {
    return (
      <div className="mb-6 rounded-xl border border-slate-200/90 bg-slate-50/80 px-4 py-10 text-center text-sm font-semibold text-slate-600">
        {loadingMsg}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
        {emptyMsg}
      </div>
    );
  }

  const chartBox = 'rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm shadow-slate-900/[0.04]';
  const titleCls = 'text-sm font-bold text-slate-800 mb-2';

  const primaryData = (agg.primary || []).map((d) => ({
    ...d,
    displayName: d.name.length > 40 ? `${d.name.slice(0, 37)}…` : d.name
  }));

  const secondaryData = (agg.secondary || []).map((d) => ({
    ...d,
    displayName: secondaryFormatter(d.name)
  }));

  return (
    <div className="mb-6 space-y-4">
      <div>
        <h2 className="text-base font-bold text-slate-900">{sectionTitle}</h2>
        <p className="mt-1 text-xs font-medium text-slate-500">{footnote}</p>
        {partial ? <p className="mt-1 text-xs font-semibold text-amber-800/90">{partialNote}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className={chartBox}>
          <h3 className={titleCls}>{t(titleKeys.primary)}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart layout="vertical" data={primaryData} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="displayName" width={100} tick={{ fontSize: 10 }} interval={0} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v) => [v, countLabel]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.name ?? ''}
                />
                <Bar dataKey="count" name={countLabel} fill={BAR_FILL} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={titleCls}>{t(titleKeys.secondary)}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart layout="vertical" data={secondaryData} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="displayName" width={100} tick={{ fontSize: 10 }} interval={0} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v) => [v, countLabel]}
                  labelFormatter={(_, payload) => secondaryFormatter(payload?.[0]?.payload?.name)}
                />
                <Bar dataKey="count" name={countLabel} fill={BAR_FILL_ALT} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={titleCls}>
            {preset === 'challenges' ? t(titleKeys.tertiaryResult) : t(titleKeys.tertiaryScore)}
          </h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              {preset === 'challenges' ? (
                <BarChart data={tertiaryResultData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [v, countLabel]} />
                  <Bar dataKey="count" name={countLabel} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {tertiaryResultData.map((entry, index) => (
                      <Cell key={`cell-r-${entry.key}`} fill={BAR_FILL_BUCKETS[index % BAR_FILL_BUCKETS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <BarChart data={tertiaryScoreData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-14} textAnchor="end" height={58} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [v, countLabel]} />
                  <Bar dataKey="count" name={countLabel} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {tertiaryScoreData.map((entry, index) => (
                      <Cell key={`cell-s-${entry.key}`} fill={BAR_FILL_BUCKETS[index % BAR_FILL_BUCKETS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={titleCls}>{t(titleKeys.timeline)}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <LineChart data={agg.timeline || []} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-16} textAnchor="end" height={54} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [v, countLabel]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  name={countLabel}
                  stroke={BAR_FILL}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSessionReportCharts;
